/*
XOL-67 (RTA redesign v2.1, V2; decisions 158 Q3, 160): the Google lookups behind the generated Site Name of a new RTA. The create form asks Google, in
the browser, what is at the requested point; rtaNominal.generatedNameFrom turns the raw answers into the base name and where it came from. Nothing
here touches Vue, the store or axios: it takes the `google` object (window.google, after gmap-vue has loaded it with the `places` library) so a
spec can hand it a fake.

  reverseGeocode(google, point)   the Geocoder's results for the point: [{ address_components: [{ long_name, types }] }], [] on ZERO_RESULTS or an error
  nearbyPlaces(google, point)     the PlacesService's nearbySearch results around the point: [{ name, types }], [] on none or an error
  hasRoute(results)               does any geocoder result name a street (a `route` component)?
  lookupPlaceResults(google, point)   { geocode, places } for generatedNameFrom. Places is asked ONLY when the geocoder named no street (the fallback
                                  of 158 Q3), so a street costs one call, not two. Never rejects: no Google, or a failed call, is { [], [] }.

A lookup is a suggestion: the SERVER numbers a repeated name (" RTA {n}") and the name to show afterwards is the stored one (decision 160).

ES5, no optional chaining or nullish coalescing (Babel 6).
------------------------------------------------------------------------ */

// How long the requested point rests before Google is asked (a drag fires a drop, a typed Lat / Lng several changes): the lookup follows the last one.
export var NAME_DEBOUNCE_MS = 600

// How long the form waits for the Google script to load before it gives up on a name from the map point (the name then stays the locality's).
export var GOOGLE_WAIT_MS = 8000

// How far around the requested point a nearby place may be: close enough to be "near" the street, far enough to find a landmark.
export var PLACES_RADIUS_M = 150

// The status of a good answer, for both services (GeocoderStatus.OK, PlacesServiceStatus.OK): the string 'OK'.
var OK = 'OK'

/** Is there a usable point? { lat, lng } of two real numbers. */
function usable (point) {
  return !!point && typeof point.lat === 'number' && typeof point.lng === 'number' && !isNaN(point.lat) && !isNaN(point.lng)
}

/**
 * The Geocoder's results for `point` ({ lat, lng }): the addresses at that spot, the most specific first, each with its address_components. An
 * answer that is not OK (ZERO_RESULTS, OVER_QUERY_LIMIT, ...), no Geocoder, or a throw is an empty list.
 */
export function reverseGeocode (google, point) {
  return new Promise(function (resolve) {
    if (!usable(point)) {
      resolve([])
      return
    }

    try {
      // No Google, or no Geocoder, throws here and is the empty list below.
      var geocoder = new google.maps.Geocoder()

      geocoder.geocode({ location: { lat: point.lat, lng: point.lng } }, function (results, status) {
        resolve(status === OK && Array.isArray(results) ? results : [])
      })
    } catch (e) {
      resolve([])
    }
  })
}

/**
 * The places near `point`: PlacesService.nearbySearch within PLACES_RADIUS_M. The service needs an element to attach to (a detached div is
 * enough: nothing is drawn). Needs the `places` library, which the map atom's gmap-vue install already asks for; without it, or on any answer
 * that is not OK, the list is empty.
 */
export function nearbyPlaces (google, point) {
  return new Promise(function (resolve) {
    if (!usable(point)) {
      resolve([])
      return
    }

    try {
      // No Google, no `places` library, or no service throws here and is the empty list below.
      var service = new google.maps.places.PlacesService(document.createElement('div'))

      service.nearbySearch({ location: { lat: point.lat, lng: point.lng }, radius: PLACES_RADIUS_M }, function (results, status) {
        resolve(status === OK && Array.isArray(results) ? results : [])
      })
    } catch (e) {
      resolve([])
    }
  })
}

/** Does any geocoder result carry a `route` (a street) address component? */
export function hasRoute (results) {
  var list = Array.isArray(results) ? results : []

  for (var i = 0; i < list.length; i++) {
    var parts = list[i] && Array.isArray(list[i].address_components) ? list[i].address_components : []

    for (var j = 0; j < parts.length; j++) {
      if (parts[j] && Array.isArray(parts[j].types) && parts[j].types.indexOf('route') !== -1) {
        return true
      }
    }
  }

  return false
}

/**
 * What Google says about the point, in the two shapes rtaNominal.generatedNameFrom reads: { geocode, places }. The geocoder is asked first; the
 * Places search only when it named no street. Resolves, never rejects.
 */
export function lookupPlaceResults (google, point) {
  return reverseGeocode(google, point).then(function (geocode) {
    if (hasRoute(geocode)) {
      return { geocode: geocode, places: [] }
    }

    return nearbyPlaces(google, point).then(function (places) {
      return { geocode: geocode, places: places }
    })
  }).catch(function () {
    return { geocode: [], places: [] }
  })
}
